import { buyColor } from "../../assets/colors";
import React, { useContext, useMemo } from "react";
import {
    Box,
    FormGroup,
    Typography,
    FormControl,
    Select,
    MenuItem,
    TextField,
} from "@mui/material";
import { useSelector } from "react-redux";
import { AppContext } from "../../App";
import { selectChartConfig } from "../../selectors/chart";
import { CandlestickSeriesOptions } from "../chartV2/chart.typings";
import ColorSelector from "../colorSelector/colorSelector";
import tinycolor from "tinycolor2";
import { KagiSeriesOptions } from "../chartV2/customSeries/kagiSeries/options";
import CommonChartSettingsTabs from './commonChartSettingsTabs';
import { chartSettingsFormGroupSx, chartSettingsRowSx } from './chartSettingsLayout';
type Props = {
    viewKey: string;
};
const KagiSettings = ({ viewKey }: Props) => {
    const appContext = useContext(AppContext)
    const chart = appContext.getChart(viewKey)
    const chartConfig = useSelector(selectChartConfig(viewKey))
    const primarySeriesGroupId = useMemo(() => {
        return chart?.primarySeriesGroup?.config?.id
    }, [chart?.primarySeriesGroup?.config?.id])

    const boxSizeMethodSettings = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            return (series as KagiSeriesOptions)?.boxMethod ?? 'atr'
        }
        return 'atr'
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const upColor = useMemo(() => {
        let color = buyColor;
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options as KagiSeriesOptions
            color = (series as any)?.upColor ?? buyColor
        }
        return tinycolor(color)
    }, [chartConfig, primarySeriesGroupId])

    const projectionUpColor = useMemo(() => {
        let color = buyColor;
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options as KagiSeriesOptions
            color = series.projectionUpColor ?? buyColor
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])
    
    const downColor = useMemo(() => {
        let color = '#ef5350';
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.downColor ?? '#ef5350'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const projectionDownColor = useMemo(() => {
        let color = '#ef5350';
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options as KagiSeriesOptions
            color = series.projectionDownColor ?? '#ef5350'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const handleColorChangeSeries = (newColor: string, type: keyof KagiSeriesOptions) => {
        if (chart) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                [type]: newColor
            } as Partial<CandlestickSeriesOptions>);
        }
    }

    const handleBoxSizeMethodChange = (method: KagiSeriesOptions["boxMethod"]) => {
        if (chart) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                boxMethod: method
            } as KagiSeriesOptions)
        }
    }

    const boxSizeValue = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options as KagiSeriesOptions
            if (boxSizeMethodSettings === 'atr') return series.atrLength
            if (boxSizeMethodSettings === 'traditional') return series.reversalAmount
            return series.percentageLTP
        }
    }, [boxSizeMethodSettings, chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const handleBoxSizeValueChange = (value: number) => {
        if (chart && value > 0) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                [boxSizeMethodSettings === 'atr' ? 'atr' : boxSizeMethodSettings === 'percentage' ? 'percentageLTP' : 'reversalAmount']: value
            } as Partial<KagiSeriesOptions>)
        }
    }

    const symbolTabContent = (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormGroup sx={chartSettingsFormGroupSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.1 }}>
                    <Typography variant="body2" sx={{ width: "160px" }}>Up bars</Typography>
                    <ColorSelector compact showOnlySwatch color={upColor.toHexString()} opacity={upColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'upColor');
                        }} />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.1 }}>
                    <Typography variant="body2" sx={{ width: "160px" }}>Down bars</Typography>
                    <ColorSelector compact showOnlySwatch color={downColor.toHexString()} opacity={downColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'downColor');
                        }} />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.1 }}>
                    <Typography variant="body2" sx={{ width: "160px" }}>Projection up bars</Typography>
                    <ColorSelector compact showOnlySwatch color={projectionUpColor.toHexString()} opacity={projectionUpColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'projectionUpColor');
                        }} />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.1 }}>
                    <Typography variant="body2" sx={{ width: "160px" }}>Projection down bars</Typography>
                    <ColorSelector compact showOnlySwatch color={projectionDownColor.toHexString()} opacity={projectionDownColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'projectionDownColor');
                        }} />
                </Box>

                <Box sx={chartSettingsRowSx}>
                    <Typography variant="body2" sx={{ width: "160px" }}>Box size method</Typography>
                    <FormControl size="small">
                        <Select
                            sx={{ height: '32px' }}
                            value={boxSizeMethodSettings}
                            onChange={(event) => handleBoxSizeMethodChange(event.target.value as KagiSeriesOptions["boxMethod"])}
                        >
                            <MenuItem value="atr">ATR</MenuItem>
                            <MenuItem value="traditional">Traditional</MenuItem>
                            <MenuItem value="percentage">Percentage LTP</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={chartSettingsRowSx}>
                    <Typography variant="body2" sx={{ width: "160px" }}>
                        {
                            boxSizeMethodSettings === 'atr' ?
                                'Atr Length' :
                                boxSizeMethodSettings === 'traditional' ?
                                    'Traditional' :
                                    'Percentage'
                        }
                    </Typography>
                    <FormControl size="small">
                        <TextField
                            fullWidth={false}
                            variant="outlined"
                            size="small"
                            value={boxSizeValue}
                            onChange={(event) => handleBoxSizeValueChange(Number(event.target.value))}
                            type="number"
                            sx={{
                                width: '80px',
                                '& .MuiInputBase-root': {
                                    height: '32px'
                                }
                            }}
                        />
                    </FormControl>
                </Box>
            </FormGroup>
        </Box>
    )

    return <CommonChartSettingsTabs viewKey={viewKey} symbolTabContent={symbolTabContent} />
}

export default KagiSettings;